import { FormEvent, useEffect, useState } from "react";
import axios from "axios";

const CENTRAL_API =
  process.env.REACT_APP_CENTRAL_API || "http://localhost:3000/api";

type Value = string | number | boolean;
type Values = Record<string, Value>;

export type SettingField = {
  name: string;
  label: string;
  type?: "text" | "email" | "password" | "number" | "toggle";
  placeholder?: string;
  hint?: string;
  // Lowest allowed value of a number field
  min?: number;
  // Used while nothing was saved yet
  defaultValue?: Value;
};

type SettingsSectionProps = {
  // Key of the setting in central-api (GET/POST /settings/:key)
  settingKey: string;
  title: string;
  description?: string;
  icon?: string;
  fields: SettingField[];
};

/**
 * Card for one central-api setting. Toggles save on click; other fields save with the button.
 */
const SettingsSection = ({
  settingKey,
  title,
  description,
  icon,
  fields,
}: SettingsSectionProps) => {
  const [values, setValues] = useState<Values>({});
  const [status, setStatus] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const url = `${CENTRAL_API}/settings/${settingKey}`;
  const hasTextFields = fields.some((field) => field.type !== "toggle");

  useEffect(() => {
    axios
      .get(url)
      .then((response) => setValues(response.data ?? {}))
      .catch(() => setStatus("⚠ Laden fehlgeschlagen"));
  }, [url]);

  const valueOf = (field: SettingField) =>
    values[field.name] ??
    field.defaultValue ??
    (field.type === "toggle" ? false : "");

  async function save(next: Values) {
    setValues(next);
    try {
      await axios.post(url, next);
      setStatus("✓ Gespeichert");
    } catch {
      setStatus("⚠ Speichern fehlgeschlagen");
    }
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save(values);
  };

  return (
    <form className="settingsCard" onSubmit={onSubmit}>
      <header className="settingsCard__header">
        {icon && <span className="settingsCard__icon">{icon}</span>}
        <div>
          <h3 className="settingsCard__title">{title}</h3>
          {description && (
            <p className="settingsCard__description">{description}</p>
          )}
        </div>
      </header>

      <div className="settingsCard__body">
        {fields.map((field) =>
          field.type === "toggle" ? (
            <label key={field.name} className="settingsToggle">
              <span className="settingsToggle__text">
                <span className="settingsField__label">{field.label}</span>
                {field.hint && (
                  <span className="settingsField__hint">{field.hint}</span>
                )}
              </span>
              <input
                type="checkbox"
                className="settingsToggle__input"
                checked={valueOf(field) === true}
                onChange={(event) =>
                  save({ ...values, [field.name]: event.target.checked })
                }
              />
              <span className="settingsToggle__switch" />
            </label>
          ) : (
            <label key={field.name} className="settingsField">
              <span className="settingsField__label">{field.label}</span>
              <span className="settingsField__control">
                <input
                  className="settingsField__input"
                  type={
                    field.type === "password"
                      ? showPassword
                        ? "text"
                        : "password"
                      : (field.type ?? "text")
                  }
                  value={String(valueOf(field))}
                  placeholder={field.placeholder}
                  min={field.min}
                  onChange={(event) =>
                    setValues({
                      ...values,
                      [field.name]:
                        field.type === "number"
                          ? Number(event.target.value)
                          : event.target.value,
                    })
                  }
                  required
                  autoComplete="off"
                />
                {field.type === "password" && (
                  <button
                    type="button"
                    className="settingsField__reveal"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                )}
              </span>
              {field.hint && (
                <span className="settingsField__hint">{field.hint}</span>
              )}
            </label>
          ),
        )}
      </div>

      {(hasTextFields || status) && (
        <footer className="settingsCard__footer">
          <span className="settingsCard__status">{status}</span>
          {hasTextFields && (
            <button type="submit" className="settingsButton">
              Speichern
            </button>
          )}
        </footer>
      )}
    </form>
  );
};

export default SettingsSection;
